import { confirmDialog, showDialog } from '@/utils/dialogs';
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { mentorAPI } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/context/ToastContext';
import { colors } from '@/design-system/theme/colors';
import type { MentorInfo, MentorInvitation } from '@/types/sharing';

interface MentorListSectionProps {
  schoolId: number;
  maxMentors: number;
  onInvitePress: () => void;
  onRefresh?: () => void;
}

/**
 * MentorListSection - Card-based section showing school mentors and pending invitations
 *
 * Features:
 * - Header with "Mentors" title, seat counter badge, and invite button
 * - List of mentor rows with avatar, name, email, remove action
 * - Pending invitations section with resend/cancel actions
 * - Confirmation dialog before removing mentor or canceling invitation
 * - Empty state with invite CTA
 * - Large touch targets for accessibility
 */
export function MentorListSection({
  schoolId,
  maxMentors,
  onInvitePress,
  onRefresh,
}: MentorListSectionProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [mentors, setMentors] = useState<MentorInfo[]>([]);
  const [invitations, setInvitations] = useState<MentorInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [mentorsData, invitationsData] = await Promise.all([
        mentorAPI.listMentors(schoolId),
        mentorAPI.listMentorInvitations(schoolId),
      ]);
      setMentors(mentorsData);
      setInvitations(invitationsData);
    } catch (error) {
      console.error('Failed to fetch mentors:', error);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRemoveMentor = async (mentor: MentorInfo) => {
    const title = t('mentors.removeTitle');
    const message = t('mentors.removeConfirm', { name: mentor.full_name });

    const confirmed = await confirmDialog(
      title,
      message,
      t('common.remove'),
      t('common.cancel'),
    );
    if (confirmed) await performRemove(mentor.id);
  };

  const performRemove = async (mentorId: number) => {
    try {
      setRemovingId(mentorId);
      await mentorAPI.removeMentor(schoolId, mentorId);
      setMentors((prev) => prev.filter((m) => m.id !== mentorId));
      onRefresh?.();
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || t('mentors.removeError');
      showDialog(errorMsg);
    } finally {
      setRemovingId(null);
    }
  };

  const handleResendInvitation = async (inv: MentorInvitation) => {
    try {
      setResendingId(inv.id);
      await mentorAPI.resendMentorInvitation(schoolId, inv.id);
      showToast(t('mentors.resendSuccess'), 'success');
      // Refresh to get updated expiry
      fetchData();
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || t('mentors.resendError');
      showDialog(errorMsg);
    } finally {
      setResendingId(null);
    }
  };

  const handleCancelInvitation = async (inv: MentorInvitation) => {
    const title = t('mentors.cancelInviteTitle');
    const message = t(
      'mentors.cancelInviteConfirm',
      { name: inv.full_name, email: inv.email }
    );

    const confirmed = await confirmDialog(
      title,
      message,
      t('common.yes'),
      t('common.no'),
    );
    if (confirmed) await performCancelInvitation(inv.id);
  };

  const performCancelInvitation = async (invitationId: number) => {
    try {
      setCancellingId(invitationId);
      await mentorAPI.cancelMentorInvitation(schoolId, invitationId);
      setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
      onRefresh?.();
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || t('mentors.cancelError');
      showDialog(errorMsg);
    } finally {
      setCancellingId(null);
    }
  };

  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const renderMentorItem = ({ item }: { item: MentorInfo }) => {
    const isRemoving = removingId === item.id;

    return (
      <View style={styles.mentorRow}>
        {/* Avatar with initials */}
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{getInitials(item.full_name)}</Text>
        </View>

        {/* Name and email */}
        <View style={styles.mentorInfo}>
          <Text style={styles.mentorName} numberOfLines={1}>
            {item.full_name}
          </Text>
          <Text style={styles.mentorEmail} numberOfLines={1}>
            {item.email}
          </Text>
        </View>

        {/* Remove button */}
        <TouchableOpacity
          onPress={() => handleRemoveMentor(item)}
          disabled={isRemoving}
          style={[styles.removeButton, isRemoving && styles.removeButtonDisabled]}
          accessibilityLabel={t('mentors.removeMentor')}
        >
          {isRemoving ? (
            <ActivityIndicator size="small" color={colors.error} />
          ) : (
            <MaterialIcons name="person-remove" size={20} color={colors.error} />
          )}
        </TouchableOpacity>
      </View>
    );
  };

  const renderInvitationItem = (inv: MentorInvitation) => {
    const isResending = resendingId === inv.id;
    const isCancelling = cancellingId === inv.id;
    const isBusy = isResending || isCancelling;

    return (
      <View key={inv.id} style={styles.mentorRow}>
        {/* Avatar with clock icon for pending */}
        <View style={[styles.avatar, styles.pendingAvatar]}>
          <MaterialIcons name="schedule" size={20} color={colors.warning} />
        </View>

        {/* Name and email */}
        <View style={styles.mentorInfo}>
          <Text style={styles.mentorName} numberOfLines={1}>
            {inv.full_name}
          </Text>
          <Text style={styles.mentorEmail} numberOfLines={1}>
            {inv.email}
          </Text>
        </View>

        {/* Resend button */}
        <TouchableOpacity
          onPress={() => handleResendInvitation(inv)}
          disabled={isBusy}
          style={[styles.resendButton, isBusy && styles.removeButtonDisabled]}
          accessibilityLabel={t('mentors.resendInvitation')}
        >
          {isResending ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : (
            <MaterialIcons name="send" size={18} color={colors.primary} />
          )}
        </TouchableOpacity>

        {/* Cancel button */}
        <TouchableOpacity
          onPress={() => handleCancelInvitation(inv)}
          disabled={isBusy}
          style={[styles.cancelButton, isBusy && styles.removeButtonDisabled]}
          accessibilityLabel={t('mentors.cancelInvitation')}
        >
          {isCancelling ? (
            <ActivityIndicator size="small" color={colors.error} />
          ) : (
            <MaterialIcons name="close" size={18} color={colors.error} />
          )}
        </TouchableOpacity>
      </View>
    );
  };

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <MaterialIcons name="school" size={48} color={colors.gray[300]} />
      <Text style={styles.emptyTitle}>
        {t('mentors.noMentors')}
      </Text>
      <Text style={styles.emptySubtitle}>
        {t('mentors.inviteFirst')}
      </Text>
      <TouchableOpacity
        onPress={onInvitePress}
        style={styles.emptyInviteButton}
        accessibilityLabel={t('mentors.invite')}
      >
        <MaterialIcons name="person-add" size={20} color="white" />
        <Text style={styles.emptyInviteButtonText}>
          {t('mentors.invite')}
        </Text>
      </TouchableOpacity>
    </View>
  );

  const totalUsed = mentors.length + invitations.length;
  const canInvite = totalUsed < maxMentors;
  const hasContent = mentors.length > 0 || invitations.length > 0;

  return (
    <Card style={styles.card}>
      <CardContent style={styles.cardContent}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <View style={styles.iconContainer}>
              <MaterialIcons name="school" size={24} color={colors.primary} />
            </View>
            <Text style={styles.headerTitle}>
              {t('mentors.title')}
            </Text>
            <View style={styles.seatBadge}>
              <Text style={styles.seatBadgeText}>
                {mentors.length}/{maxMentors}
              </Text>
            </View>
          </View>

          {canInvite && hasContent && (
            <TouchableOpacity
              onPress={onInvitePress}
              style={styles.inviteButton}
              accessibilityLabel={t('mentors.invite')}
            >
              <MaterialIcons name="person-add" size={18} color="white" />
              <Text style={styles.inviteButtonText}>
                {t('mentors.invite')}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Content */}
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : !hasContent ? (
          renderEmpty()
        ) : (
          <View>
            {/* Active Mentors */}
            <FlatList
              data={mentors}
              keyExtractor={(item) => String(item.id)}
              renderItem={renderMentorItem}
              scrollEnabled={false}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
            />

            {/* Pending Invitations */}
            {invitations.length > 0 && (
              <View style={styles.pendingSection}>
                <View style={styles.pendingSectionHeader}>
                  <MaterialIcons name="schedule" size={16} color={colors.warning} />
                  <Text style={styles.pendingSectionTitle}>
                    {t('mentors.pendingInvitations')} ({invitations.length})
                  </Text>
                </View>
                {invitations.map((inv, index) => (
                  <View key={inv.id}>
                    {index > 0 && <View style={styles.separator} />}
                    {renderInvitationItem(inv)}
                  </View>
                ))}
              </View>
            )}
          </View>
        )}
      </CardContent>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    marginBottom: 16,
  },
  cardContent: {
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: `${colors.primary}15`,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text.primary,
  },
  seatBadge: {
    backgroundColor: colors.gray[100],
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  seatBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text.secondary,
  },
  inviteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: colors.primary,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
  },
  inviteButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  mentorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  pendingAvatar: {
    backgroundColor: `${colors.warning}20`,
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  mentorInfo: {
    flex: 1,
    marginRight: 12,
  },
  mentorName: {
    fontSize: 15,
    fontWeight: '500',
    color: colors.text.primary,
    marginBottom: 2,
  },
  mentorEmail: {
    fontSize: 13,
    color: colors.text.muted,
  },
  removeButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: `${colors.error}10`,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: `${colors.primary}10`,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  cancelButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: `${colors.error}10`,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeButtonDisabled: {
    opacity: 0.5,
  },
  separator: {
    height: 1,
    backgroundColor: colors.border,
    marginLeft: 56,
  },
  pendingSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  pendingSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  pendingSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.secondary,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginTop: 12,
    marginBottom: 4,
  },
  emptySubtitle: {
    fontSize: 14,
    color: colors.text.muted,
    textAlign: 'center',
    marginBottom: 20,
  },
  emptyInviteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  emptyInviteButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: 'white',
  },
});

export default MentorListSection;
